import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiFilm, FiVideo, FiLoader, FiDownload, FiTrash2, FiPlus, FiArrowUp, FiArrowDown, FiZap, FiInfo, FiMic, FiActivity } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { takeHandoff } from "../video/script";
import { useJobHistory, formatSeconds } from "./audioShared";
import { StudioPanel, LANGUAGES, FALLBACK_STYLES, RATIOS, inputClass, labelClass, ghost, card, slug, saveBlob, Pills, Slider } from "./VideoStudioPanel";

export default function VideoGeneratorTool() {
  const { clientId, history: everything, loading, elapsed, add, remove } = useJobHistory("video");
  const history = useMemo(() => everything.filter((job) => job.quality !== "images"), [everything]);   // videos made from the visitor's own pictures belong to the other tool
  const running = history.find((job) => job.status === "queued" || job.status === "processing");
  const latest = history[0];
  const [options, setOptions] = useState(null);
  const [mode, setMode] = useState("cinematic");
  const [voices, setVoices] = useState([]);
  // cinematic
  const [script, setScript] = useState("");
  const [sceneCount, setSceneCount] = useState(5);
  const [style, setStyle] = useState("cinematic");
  const [ratio, setRatio] = useState("16:9");
  const [language, setLanguage] = useState("en");
  const [voice, setVoice] = useState("");
  const [rate, setRate] = useState(0);
  const [board, setBoard] = useState(null);
  const [boardBusy, setBoardBusy] = useState(false);

  // a script sent here from the Video Script Generator
  useEffect(() => {
    const sent = takeHandoff("text");
    if (sent) { setMode("cinematic"); setScript(sent.text); }
  }, []);
  // clip
  const [prompt, setPrompt] = useState("");
  const [clipStyle, setClipStyle] = useState("cinematic");
  const [clipRatio, setClipRatio] = useState("16:9");
  const [seconds, setSeconds] = useState(3);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [viewId, setViewId] = useState(null);
  const [detail, setDetail] = useState(null);
  const resultRef = useRef(null);

  const styles = options ? options.styles.map((s) => [s.key, s.label]) : FALLBACK_STYLES;
  const maxScenes = options?.max_scenes || 8;
  const minScenes = options?.min_scenes || 2;

  useEffect(() => {
    let alive = true;
    apiRequest("/core/video/options/").then(({ ok, data }) => { if (alive && ok) setOptions(data); });
    apiRequest("/core/tts/voices/").then(({ ok, data }) => { if (alive && ok) setVoices(data.voices || []); });
    return () => { alive = false; };
  }, []);

  const languages = useMemo(() => [...new Set(voices.map((v) => v.language))].filter((l) => LANGUAGES[l]), [voices]);
  const voicesHere = useMemo(() => voices.filter((v) => v.language === language), [voices, language]);
  useEffect(() => {
    if (!voicesHere.length) return;
    if (voice === "none" || voicesHere.some((v) => v.id === voice)) return;
    setVoice((voicesHere.find((v) => v.gender === "female") || voicesHere[0]).id);
  }, [voicesHere, voice]);

  useEffect(() => {
    if (latest && latest.status === "completed") setViewId(latest.id);
  }, [latest?.id, latest?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!viewId) { setDetail(null); return undefined; }
    let alive = true;
    apiRequest(`/core/voice-clone/${viewId}/?client_id=${encodeURIComponent(clientId)}`).then(({ ok, data }) => { if (alive) setDetail(ok ? data : null); });
    return () => { alive = false; };
  }, [viewId, clientId]);

  // ---------------------------------------------------------------------------------------- storyboard
  const makeBoard = async () => {
    setFieldError("");
    setError("");
    setBoardBusy(true);
    const { ok, data } = await apiRequest("/core/video/storyboard/", { method: "POST", body: { script, scenes: sceneCount, style, ratio } });
    setBoardBusy(false);
    if (!ok) return setFieldError(data?.script || errorMessage(data));
    setBoard({ title: data.title || "My video", look: data.look || "", engine: data.engine, scenes: data.scenes.map((s) => ({ ...s, seconds: 0, motion: "auto", transition: "fade" })) });
    return setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };
  const editScene = (i, patch) => setBoard((b) => ({ ...b, scenes: b.scenes.map((s, k) => (k === i ? { ...s, ...patch } : s)) }));
  const moveScene = (i, d) => setBoard((b) => {
    const scenes = [...b.scenes];
    const j = i + d;
    if (j < 0 || j >= scenes.length) return b;
    [scenes[i], scenes[j]] = [scenes[j], scenes[i]];
    return { ...b, scenes };
  });
  const removeScene = (i) => setBoard((b) => (b.scenes.length > minScenes ? { ...b, scenes: b.scenes.filter((_, k) => k !== i) } : b));
  const addScene = () => setBoard((b) => (b.scenes.length < maxScenes ? { ...b, scenes: [...b.scenes, { text: "", visual: "", caption: "", action: "", speaks: false, seconds: 0, motion: "auto", transition: "fade" }] } : b));

  const send = async (fields) => {
    setError("");
    setFieldError("");
    setSubmitting(true);
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "video");
    Object.entries(fields).forEach(([k, v]) => form.append(k, typeof v === "object" ? JSON.stringify(v) : String(v)));
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) { setFieldError(data?.prompt || errorMessage(data)); return; }
    setViewId(null);
    add(data);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };
  const makePictures = () => send({ mode: "scenes", title: board.title, look: board.look, style, ratio, voice: voice === "none" ? "" : voice, rate, scenes: board.scenes });
  const makeClip = () => send({ mode: "clip", prompt, style: clipStyle, ratio: clipRatio, seconds });

  const view = detail && detail.status === "completed" ? detail : null;
  const segments = view?.segments && !Array.isArray(view.segments) ? view.segments : {};
  const runningMode = running ? (running.quality === "clip" ? "clip" : "scenes") : "";
  const canBoard = script.trim().length >= 12 && !boardBusy;
  const canClip = prompt.trim().length >= 5 && !submitting && !running;
  const canPictures = board && board.scenes.every((s) => s.text.trim() || s.visual.trim()) && !submitting && !running;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Text-to-Video Generation</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-9">
        Turn a script into a finished video: realistic scene pictures, an AI narrator, smooth camera moves, transitions and captions - or ask an AI video model for a short clip from one sentence.
      </p>

      <div className="flex justify-center mb-6">
        <div role="tablist" className="inline-flex rounded-full bg-white/5 border border-navy-border p-1">
          {[["cinematic", "Cinematic video from a script", FiFilm], ["clip", "AI video clip from a sentence", FiVideo]].map(([key, label, Icon]) => (
            <button key={key} role="tab" aria-selected={mode === key} type="button" onClick={() => { setMode(key); setFieldError(""); }}
              className={`px-4 md:px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${mode === key ? "bg-brand-gradient text-white shadow-md shadow-accent/20" : "text-gray-300 hover:text-white"}`}><Icon size={15} /> {label}</button>
          ))}
        </div>
      </div>

      {/* ------------------------------------------------------------------ cinematic: the brief */}
      {mode === "cinematic" && (
        <div className={`${card} space-y-6`}>
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <label htmlFor="video-script" className="text-xs uppercase tracking-widest text-white/50 font-semibold">Your topic or script</label>
              <span className="text-xs text-gray-500">{script.length} / {options?.max_script || 2500}</span>
            </div>
            <textarea id="video-script" className={`${inputClass} min-h-[130px] leading-relaxed`} value={script} dir="auto" maxLength={options?.max_script || 2500}
              onChange={(e) => setScript(e.target.value)} placeholder="e.g. Introduce our new coffee shop: fresh beans roasted every morning, a warm friendly place, and a special opening week offer." />
            <div className="flex flex-wrap gap-2 mt-3">
              {(options?.script_ideas || []).slice(0, 4).map((idea) => <button key={idea} type="button" onClick={() => setScript(idea)} className="px-3 py-1 rounded-full text-xs border border-navy-border text-gray-300 hover:border-accent-light text-start">{idea.length > 58 ? `${idea.slice(0, 56)}...` : idea}</button>)}
            </div>
            <p className="text-xs text-gray-500 mt-3">Write in any language - the narrator speaks it. A short topic is fine: the storyboard writer turns it into scenes.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div><span className={labelClass}>Look</span><Pills label="Look" value={style} onChange={setStyle} options={styles} /></div>
            <div>
              <span className={labelClass}>Shape</span>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Shape">
                {RATIOS.map(([key, label, hint]) => (
                  <button key={key} type="button" onClick={() => setRatio(key)} aria-pressed={ratio === key} className={`px-3.5 py-2 rounded-xl border text-start transition-colors ${ratio === key ? "border-accent-light bg-accent/10" : "border-navy-border bg-white/5 hover:border-accent-light/60"}`}>
                    <span className="block text-sm font-semibold">{label}</span><span className="block text-[11px] text-gray-400">{hint}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-[1fr_1.4fr_1fr] gap-5 items-end">
            <Slider label="Number of scenes" value={sceneCount} min={minScenes} max={maxScenes} onChange={setSceneCount} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="video-lang" className={labelClass}>Narrator language</label>
                <select id="video-lang" className={inputClass} value={language} onChange={(e) => { setLanguage(e.target.value); setVoice(""); }}>
                  {languages.length ? languages.map((l) => <option key={l} value={l}>{LANGUAGES[l]}</option>) : <option value="en">English</option>}
                </select>
              </div>
              <div>
                <label htmlFor="video-voice" className={labelClass}>Voice</label>
                <select id="video-voice" className={inputClass} value={voice} onChange={(e) => setVoice(e.target.value)}>
                  {voicesHere.map((v) => <option key={v.id} value={v.id}>{v.name.split(" - ")[0]}{v.accent_label ? ` (${v.accent_label})` : ""}, {v.gender}</option>)}
                  <option value="none">No narration (silent)</option>
                </select>
              </div>
            </div>
            <Slider label="Speaking speed" value={rate} min={-30} max={40} step={5} suffix="%" onChange={setRate} />
          </div>

          {fieldError && <p className="text-sm text-amber-300">{fieldError}</p>}
          <button type="button" onClick={makeBoard} disabled={!canBoard}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
            {boardBusy ? <><FiLoader className="animate-spin" /> Writing the storyboard...</> : <><FiZap /> {board ? "Write a new storyboard" : "Create the storyboard"}</>}
          </button>
        </div>
      )}

      {/* ------------------------------------------------------------------ clip: the brief */}
      {mode === "clip" && (
        <div className={`${card} space-y-6`}>
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <label htmlFor="clip-prompt" className="text-xs uppercase tracking-widest text-white/50 font-semibold">Describe the clip</label>
              <span className="text-xs text-gray-500">{prompt.length} / {options?.max_prompt || 400}</span>
            </div>
            <textarea id="clip-prompt" className={`${inputClass} min-h-[100px] leading-relaxed`} value={prompt} dir="auto" maxLength={options?.max_prompt || 400}
              onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. Waves crashing on a rocky coast at sunset, drone shot" />
            <div className="flex flex-wrap gap-2 mt-3">
              {(options?.ideas || []).slice(0, 5).map((idea) => <button key={idea} type="button" onClick={() => setPrompt(idea)} className="px-3 py-1 rounded-full text-xs border border-navy-border text-gray-300 hover:border-accent-light">{idea.length > 48 ? `${idea.slice(0, 46)}...` : idea}</button>)}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div><span className={labelClass}>Look</span><Pills label="Look" value={clipStyle} onChange={setClipStyle} options={styles} /></div>
            <div className="space-y-5">
              <div><span className={labelClass}>Shape</span><Pills label="Shape" value={clipRatio} onChange={setClipRatio} options={RATIOS.map(([k, l]) => [k, l])} /></div>
              <Slider label="Length" value={seconds} min={2} max={6} suffix=" s" onChange={setSeconds} />
            </div>
          </div>
          <p className="text-xs text-gray-400 bg-white/5 border border-navy-border rounded-xl px-4 py-3 flex gap-2"><FiInfo className="shrink-0 mt-0.5" size={14} />
            AI video models need powerful graphics cards, so this uses free shared ones. The best (LTX-Video, CogVideoX) are tried first; when they are out of free capacity a smaller model
            (ZeroScope: about 3 seconds, low resolution) makes the clip. For longer, sharper, realistic videos use <button type="button" className="underline text-accent-light" onClick={() => setMode("cinematic")}>the cinematic video</button>.</p>
          {fieldError && <p className="text-sm text-amber-300">{fieldError}</p>}
          <button type="button" onClick={makeClip} disabled={!canClip}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? <><FiLoader className="animate-spin" /> Sending...</> : running ? <><FiLoader className="animate-spin" /> Making a video...</> : <><FiVideo /> Generate the clip</>}
          </button>
        </div>
      )}
      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6">{error}</p>}
      <p className="text-xs text-gray-500 text-center mt-3">Free AI services make the pictures, the clips and the voice, so do not enter private or confidential text. Violence, sexual content and requests that undress people are refused.
        AI videos can contain mistakes - check them before you publish, and do not present them as real footage of real events.</p>

      <div ref={resultRef} />

      {/* ------------------------------------------------------------------ the storyboard */}
      {mode === "cinematic" && board && (
        <div className={`${card} mt-8`}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
            <div className="min-w-0 flex-1">
              <label htmlFor="board-title" className={labelClass}>Video title</label>
              <input id="board-title" className={`${inputClass} max-w-md`} value={board.title} maxLength={80} onChange={(e) => setBoard({ ...board, title: e.target.value })} />
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-200">Storyboard by {board.engine === "built-in" ? "the built-in writer" : board.engine}</span>
          </div>
          {board.engine === "built-in" && <p className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-4">The AI writer was not reachable, so the storyboard was made from your own sentences. Check each scene's picture description - it is what the picture is drawn from.</p>}
          <div className="mb-5">
            <label htmlFor="board-look" className={labelClass}>The look shared by all scenes <span className="normal-case tracking-normal text-gray-500 font-normal">(keeps the pictures consistent)</span></label>
            <input id="board-look" className={inputClass} value={board.look} maxLength={300} onChange={(e) => setBoard({ ...board, look: e.target.value })} placeholder="e.g. a young woman with dark hair in a cream apron, a warm wooden coffee shop, soft morning light" dir="auto" />
          </div>
          <ol className="space-y-4">
            {board.scenes.map((scene, i) => (
              <li key={i} className="rounded-xl border border-navy-border bg-navy/60 p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold">Scene {i + 1}</p>
                  <div className="flex gap-1.5">
                    <button type="button" onClick={() => moveScene(i, -1)} disabled={i === 0} className={ghost} aria-label={`Move scene ${i + 1} up`}><FiArrowUp size={13} /></button>
                    <button type="button" onClick={() => moveScene(i, 1)} disabled={i === board.scenes.length - 1} className={ghost} aria-label={`Move scene ${i + 1} down`}><FiArrowDown size={13} /></button>
                    <button type="button" onClick={() => removeScene(i)} disabled={board.scenes.length <= minScenes} className={ghost} aria-label={`Delete scene ${i + 1}`}><FiTrash2 size={13} /></button>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={`sc-text-${i}`} className={labelClass}><FiMic className="inline -mt-0.5" size={11} /> What the narrator says</label>
                    <textarea id={`sc-text-${i}`} className={`${inputClass} min-h-[90px]`} value={scene.text} maxLength={420} dir="auto" onChange={(e) => editScene(i, { text: e.target.value })} />
                  </div>
                  <div>
                    <label htmlFor={`sc-vis-${i}`} className={labelClass}>What the viewer sees</label>
                    <textarea id={`sc-vis-${i}`} className={`${inputClass} min-h-[90px]`} value={scene.visual} maxLength={420} dir="auto" onChange={(e) => editScene(i, { visual: e.target.value })} placeholder="A concrete picture: who, where, what light" />
                  </div>
                </div>
                <div className="mt-3 grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={`sc-act-${i}`} className={labelClass}><FiActivity className="inline -mt-0.5" size={11} /> What moves <span className="normal-case tracking-normal text-gray-500 font-normal">(hands, legs, lips... used to bring the picture to life)</span></label>
                    <input id={`sc-act-${i}`} className={inputClass} value={scene.action || ""} maxLength={220} dir="auto" onChange={(e) => editScene(i, { action: e.target.value })} placeholder="e.g. she pours the coffee with her right hand, smiles and nods" />
                    <label className="flex items-start gap-2 text-xs text-gray-300 mt-2.5 cursor-pointer"><input type="checkbox" checked={!!scene.speaks} onChange={(e) => editScene(i, { speaks: e.target.checked })} className="mt-0.5 accent-[#22d3ee] w-4 h-4" /> <span>The person on screen says this line (the lips follow the words)</span></label>
                  </div>
                  <div>
                    <label htmlFor={`sc-cap-${i}`} className={labelClass}>Short caption (optional)</label>
                    <input id={`sc-cap-${i}`} className={inputClass} value={scene.caption} maxLength={90} dir="auto" onChange={(e) => editScene(i, { caption: e.target.value })} />
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap gap-3 mt-4">
            <button type="button" onClick={addScene} disabled={board.scenes.length >= maxScenes} className={ghost}><FiPlus size={13} /> Add a scene</button>
          </div>
          {fieldError && <p className="text-sm text-amber-300 mt-4">{fieldError}</p>}
          <button type="button" onClick={makePictures} disabled={!canPictures}
            className="w-full mt-5 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? <><FiLoader className="animate-spin" /> Sending...</> : running ? <><FiLoader className="animate-spin" /> Making the pictures and voice...</> : <><FiFilm /> Make the pictures and the voice ({board.scenes.length} scenes)</>}
          </button>
          <p className="text-xs text-gray-500 text-center mt-2">Takes about {Math.max(1, Math.round(board.scenes.length * 0.5))}-{Math.max(2, board.scenes.length * 2)} minutes: one realistic picture is drawn for every scene. Then you can preview the video, bring its characters to life (moving hands, legs and lips) and download it.</p>
        </div>
      )}

      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6 flex items-center gap-3">
          <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
          <div>
            <p className="font-semibold">{runningMode === "clip" ? "Making your clip" : "Making the pictures and the voice"}... {formatSeconds(elapsed)}{running.progress && <span className="text-gray-400 font-normal"> · {running.progress}</span>}</p>
            <p className="text-sm text-gray-400">{runningMode === "clip" ? "Usually under a minute; longer when the free video models are busy." : "Each scene's picture is drawn by a free image service, so this takes a few minutes. You can leave this page open."}</p>
          </div>
        </div>
      )}
      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3"><FiInfo className="mt-0.5 shrink-0" /><span>{latest.error_message || "The video could not be made. Please try again."}</span></div>
      )}

      {/* ------------------------------------------------------------------ results */}
      {view && !running && segments.mode === "clip" && <ClipResult view={view} segments={segments} />}
      {view && !running && segments.mode === "scenes" && <StudioPanel key={view.id} view={view} segments={segments} clientId={clientId} />}

      <div className="mt-12">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your videos</p>
        {loading ? <p className="text-sm text-gray-400">Loading...</p> : history.length === 0 ? <p className="text-sm text-gray-500">Nothing here yet. Your videos will appear here.</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {history.map((job) => (
              <div key={job.id} className={`rounded-xl bg-white/5 border overflow-hidden ${viewId === job.id ? "border-accent-light/60" : "border-navy-border"}`}>
                {job.status === "completed" ? (
                  <button type="button" onClick={() => setViewId(job.id)} className="block w-full relative bg-black" aria-label={`Open ${job.original_name}`}>
                    {job.video_url ? <video src={`${job.video_url}#t=0.1`} muted preload="metadata" className="w-full aspect-video object-cover pointer-events-none" /> : job.images?.[0] ? <img src={job.images[0].thumb} alt="" className="w-full aspect-video object-cover" loading="lazy" /> : <div className="aspect-video" />}
                    <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-black/60 text-[10px] font-semibold">{job.quality === "clip" ? "AI clip" : "Cinematic"}</span>
                  </button>
                ) : (
                  <div className="w-full aspect-video flex items-center justify-center text-xs text-gray-500 px-3 text-center bg-navy">
                    {job.status === "failed" ? <span className="text-red-300">{job.error_message}</span> : <span className="inline-flex items-center gap-2"><FiLoader className="animate-spin" /> Working...</span>}
                  </div>
                )}
                <div className="p-3">
                  <p className="text-xs text-gray-200 line-clamp-2 min-h-[2rem]" dir="auto">{job.original_name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-gray-500">{new Date(job.created_at).toLocaleDateString()}</span>
                    {job.status !== "processing" && job.status !== "queued" && <button onClick={() => { if (viewId === job.id) setViewId(null); remove(job); }} title="Delete" className="text-gray-400 hover:text-red-400"><FiTrash2 size={14} /></button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ an AI clip
function ClipResult({ view, segments }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try { saveBlob(`${slug(view.original_name)}.mp4`, await (await fetch(view.video_url)).blob()); } catch { window.open(view.video_url, "_blank", "noopener"); }
    setBusy(false);
  };
  return (
    <div className={`${card} mt-6 border-accent-light/30`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0"><p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1">Your clip</p><p className="font-semibold" dir="auto">{view.original_name}</p></div>
        {view.engine && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-200">{view.engine}</span>}
      </div>
      {segments.notice && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-4">{segments.notice}</p>}
      <div className="rounded-xl overflow-hidden bg-black flex justify-center"><video src={view.video_url} controls loop autoPlay muted playsInline className="max-h-[560px] w-auto max-w-full" /></div>
      <div className="flex flex-wrap gap-2 mt-4">
        <button type="button" onClick={download} disabled={busy} className={ghost}>{busy ? <FiLoader className="animate-spin" size={13} /> : <FiDownload size={13} />} Download MP4</button>
      </div>
    </div>
  );
}

